import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Wrench } from "lucide-react";

const heroImage = "/hero-warehouse.webp";

/**
 * Search-first homepage hero. Product search is the primary above-the-fold
 * action, backed by inline part-number suggestions. Trust metrics and
 * certifications flank the search to establish immediate authority
 * (benchmark: Radwell, DO Supply, EU Automation, MRO Electric).
 */
const BrandHero = () => {
  return (
    <section className="relative overflow-hidden" aria-label="Industrial automation parts">
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt="Nardy Automation US warehouse — industrial automation parts"
          className="w-full h-full object-cover"
          width={1920}
          height={800}
          fetchPriority="high"
          loading="eager"
          decoding="async"
        />
        {/* Subtle dark overlay + stronger left gradient for headline legibility */}
        <div className="absolute inset-0 bg-surface-darker/45" />
        <div className="absolute inset-0 bg-gradient-to-r from-surface-darker/85 via-surface-darker/50 to-transparent" />
      </div>

      <div className="container relative z-10 pt-16 md:pt-24 lg:pt-28 pb-0">
        <div className="max-w-2xl">
          <p className="font-display font-semibold uppercase tracking-[0.14em] text-white text-sm md:text-base mb-4">
            Thousands of Automation Parts
          </p>
          <h1 className="font-display font-black text-white uppercase leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl mb-10">
            Delivered
            <span className="block">Fast.</span>
          </h1>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8">
            <Link to="/request-quote" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="bg-brand text-white font-semibold text-base px-8 py-6 rounded-md hover:bg-brand/90 transition-colors w-full sm:w-auto"
              >
                Get a Fast Quote
              </Button>
            </Link>
            <Link to="/industrial-automation-parts" className="w-full sm:w-auto">
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-foreground font-semibold text-base px-8 py-6 rounded-md w-full sm:w-auto transition-colors"
              >
                Learn More
              </Button>
            </Link>
          </div>

          <p className="text-white/85 text-base md:text-lg leading-relaxed max-w-md font-medium">
            Save money on downtime with the most trusted leading experts in
            automation replacement and repair.
          </p>
        </div>

        {/* BUY / REPAIR / SELL bottom strip — anchored to hero, right-aligned on desktop */}
        <div className="mt-14 md:mt-20 lg:mt-24 lg:ml-auto lg:max-w-3xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 bg-surface-darker/85 border-t border-white/10">
            <Link
              to="/industrial-automation-parts"
              className="group p-6 md:p-7 border-b sm:border-b-0 sm:border-r border-white/10 hover:bg-white/[0.05] transition-colors"
            >
              <div className="flex items-center gap-2 mb-2">
                <ShoppingCart className="w-4 h-4 text-brand" aria-hidden="true" />
                <p className="text-white font-display font-bold text-lg md:text-xl">Buy</p>
              </div>
              <p className="text-white/75 text-sm leading-snug">
                Get thousands of automation parts delivered fast.
              </p>
            </Link>
            <Link
              to="/industrial-automation-repair-services"
              className="group p-6 md:p-7 hover:bg-white/[0.05] transition-colors"
            >
              <div className="flex items-center gap-2 mb-2">
                <Wrench className="w-4 h-4 text-brand" aria-hidden="true" />
                <p className="text-white font-display font-bold text-lg md:text-xl">Repair</p>
              </div>
              <p className="text-white/75 text-sm leading-snug">
                Get your automation parts repaired quickly.
              </p>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandHero;
